import React, { useEffect, useState } from 'react'
import { Button, Div, P } from '@components/StyledTags'
import { useTranslatedTerms } from '@hooks'
import {
  CONSENT_PREFERENCES_EVENT,
  DENIED_CONSENT,
  GRANTED_CONSENT,
  isSSR,
  readConsentCookie,
  updateGoogleConsent,
  writeConsentCookie,
} from '@utils'
import { colors } from '@theme'

const LEGACY_LOCAL_STORAGE_KEY = 'acceptedTerms'

/**
 * Consent recorded by the previous banner is only carried over when the visitor
 * refused: that refusal was never actually enforced, so honouring it now is
 * strictly more protective. A previous acceptance is deliberately NOT carried
 * over - it was collected by a banner that did not gate anything, so we ask
 * again rather than rely on it.
 */
const migrateLegacyRefusal = () => {
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY))
    if (!legacy?.answeredPrompt || legacy?.acceptedTerms) return null

    writeConsentCookie(DENIED_CONSENT)
    return DENIED_CONSENT
  } catch {
    return null
  }
}

const Cookies = () => {
  const { getTranslatedTerm } = useTranslatedTerms()
  // Undefined until the client has read the cookie, so the banner never renders
  // during SSR or before we know whether it is needed.
  const [consent, setConsent] = useState(undefined)

  useEffect(() => {
    setConsent(readConsentCookie() || migrateLegacyRefusal() || null)
  }, [])

  // Re-open on request from the footer's "Cookie Settings" link.
  useEffect(() => {
    const reopen = () => setConsent(null)

    window.addEventListener(CONSENT_PREFERENCES_EVENT, reopen)
    return () => window.removeEventListener(CONSENT_PREFERENCES_EVENT, reopen)
  }, [])

  const recordConsent = nextConsent => {
    writeConsentCookie(nextConsent)
    updateGoogleConsent(nextConsent)
    setConsent(nextConsent)
  }

  const acceptTerms = () => recordConsent(GRANTED_CONSENT)
  const rejectTerms = () => recordConsent(DENIED_CONSENT)

  if (isSSR()) return null
  // `undefined` = still reading, `null` = no record yet, object = answered.
  if (consent === undefined || consent) return null

  return (
    <Div
      position="fixed"
      left="0"
      right="0"
      bottom="0"
      display="flex"
      flexDirection={['column', null, null, 'row']}
      justifyContent="space-between"
      p="18px"
      bg={colors.grayLt}
      // needs to be above userway
      zIndex="9999999999"
    >
      <Div width={['100%', null, null, '78%']}>
        <P
          fontSize="14px"
          fontFamily="quaternary"
          mb="15px"
          color={colors.black}
        >
          {getTranslatedTerm('cookieLine1')}
        </P>
        <P
          fontSize="14px"
          fontFamily="quaternary"
          mb="15px"
          color={colors.black}
        >
          {getTranslatedTerm('cookieLine2')}
        </P>
      </Div>
      <Div
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="flex-end"
      >
        <Div
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          mt={[3, null, null, 0]}
        >
          <Button
            dim85Hover
            py="13px"
            px={['16px', null, null, '25px']}
            fontSize="14px"
            bg={colors.navy}
            color={colors.white}
            lineHeight="22px"
            // Translated labels are longer than the English original and would
            // otherwise wrap mid-word (notably ja/zh, which have no spaces).
            whiteSpace="nowrap"
            onClick={acceptTerms}
          >
            {getTranslatedTerm('I ACCEPT')}
          </Button>
          <Button
            enableHoverUnderline
            py="13px"
            px={['16px', null, null, '25px']}
            fontSize="14px"
            fontWeight="normal"
            color={colors.navy}
            lineHeight="22px"
            whiteSpace="nowrap"
            onClick={rejectTerms}
          >
            {getTranslatedTerm('I Refuse')}
          </Button>
        </Div>
      </Div>
    </Div>
  )
}

export default Cookies
